import React, { useCallback, useEffect, useMemo, useState } from "react";
import mesApi from "../../../Api/mes.api";
import {
  TimelineBatch,
  IngredientLot,
  LotWarehouseStock,
  RecipeMaterialLines,
} from "../../../types/mes";
import { toast } from "react-toastify";
import { compareLocale, dateLocale, t } from "../../../i18n/locale";

interface LotAssignmentProps {
  batches: TimelineBatch[];
  /** Plan being confirmed — the inter-warehouse transfer is booked against it. */
  timelineId: number;
  productionDate: string;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Two kinds of material are assigned here, and they must stay distinguishable
 * all the way to the database:
 *  - `ingredient` — a recipe line, scaled by baker %, keyed by ingredient;
 *  - `phase_material` — the extra material a phase consumes (packaging, repro),
 *    keyed by an alias because it usually has no recipe ingredient at all, and
 *    quantified by the product's normative (per piece / per batch / per kg).
 */
type MaterialKind = "ingredient" | "phase_material";

/** One material of one batch — the granularity that is actually persisted. */
interface AssignmentRow {
  kind: MaterialKind;
  batch_id: number;
  batch_name: string;
  product_key: number;
  product_name: string;
  /** Identifies the material within a product; drives grouping in shared mode. */
  material_key: string;
  /** Which lot pool this row draws from (`ing:<id>` or `alias:<id>`). */
  lot_pool_key: string;
  ingredient_id: number | null;
  alias_id: number | null;
  /** Normative row behind a phase material — required to book it as such. */
  phase_material_id: number | null;
  phase_name: string | null;
  material_name: string;
  required_qty: number;
  /** Phase materials are not necessarily in kilograms. */
  unit_label: string;
  selected_lot_id: number | null;
}

/** Header info per product, so the planner can verify the recipe that was pulled. */
interface ProductRecipeInfo {
  product_key: number;
  product_name: string;
  recipe_label: string;
  batch_count: number;
}

const ingredientPoolKey = (ingredientId: number): string => `ing:${ingredientId}`;
const aliasPoolKey = (aliasId: number): string => `alias:${aliasId}`;

/**
 * Water is the single exception ingredient: it is always available in unlimited
 * quantity and is never tracked by lot, so it is excluded from lot assignment.
 * Mirrors the backend `isWaterType` helper (mesRecipeUtils.ts).
 */
const isWaterIngredient = (typeName?: string | null, name?: string | null): boolean => {
  const t = (typeName || "").trim().toLowerCase();
  const n = (name || "").trim().toLowerCase();
  return t === "water" || t === "voda" || n.includes("voda") || n.includes("water");
};

/** FEFO order: earliest expiry first, lots without an expiry date last. */
const byFefo = (a: IngredientLot, b: IngredientLot): number => {
  if (a.expiry_date && b.expiry_date) {
    return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
  }
  if (a.expiry_date) return -1;
  if (b.expiry_date) return 1;
  return new Date(a.delivery_date).getTime() - new Date(b.delivery_date).getTime();
};

/**
 * The server reports each lot's stock at the two warehouses of the production
 * flow, ordered by priority: 0 is the bakery's own material warehouse, 1 the
 * central one. Priority 0 can go straight into a mix, while anything taken from
 * priority 1 has to travel on an inter-warehouse transfer first — which is why
 * lots are offered in that order and why the central ones end up in the transfer
 * panel at the bottom.
 *
 * The short labels follow the priority, not the warehouse names, so renaming a
 * warehouse cannot make them lie.
 */
const AT_BAKERY = 0;
const AT_CENTRAL = 1;
const WAREHOUSE_LABEL: Record<number, string> = { [AT_BAKERY]: "pekara", [AT_CENTRAL]: "centralno" };

/** Balances below this are rounding noise, not usable stock. */
const STOCK_EPSILON = 0.001;
/** Shortages below this are rounding noise, not a real gap. */
const SHORTAGE_EPSILON = 0.01;

const round3 = (kg: number): number => Math.round(kg * 1000) / 1000;

const stockAt = (lot: IngredientLot, priority: number): LotWarehouseStock | undefined =>
  lot.stock_by_warehouse?.find((s) => s.priority === priority);

const kgAt = (lot: IngredientLot, priority: number): number =>
  Number(stockAt(lot, priority)?.remaining_kg ?? 0);

/**
 * Where a lot would be taken from.
 *
 * `elsewhere` means it has stock, but at a warehouse outside the production pair
 * (e.g. slastičarna) — it is physically somewhere else. `unbooked` means no
 * movement was ever booked for the lot: the stock record is missing rather than
 * the stock, so such a lot stays selectable — hiding it would block ingredients
 * that are physically there.
 */
type LotLocation = "bakery" | "central" | "elsewhere" | "unbooked";

const lotLocation = (lot: IngredientLot): LotLocation => {
  if (kgAt(lot, AT_BAKERY) > STOCK_EPSILON) return "bakery";
  if (kgAt(lot, AT_CENTRAL) > STOCK_EPSILON) return "central";
  if (lot.stock_elsewhere?.some((s) => Number(s.remaining_kg) > STOCK_EPSILON)) {
    return "elsewhere";
  }
  return "unbooked";
};

/** The warehouse outside the pair that holds most of this lot. */
const elsewhereStock = (lot: IngredientLot): LotWarehouseStock | undefined =>
  (lot.stock_elsewhere || [])
    .slice()
    .sort((a, b) => Number(b.remaining_kg) - Number(a.remaining_kg))[0];

const LOCATION_RANK: Record<LotLocation, number> = {
  bakery: 0,
  central: 1,
  unbooked: 2,
  elsewhere: 3,
};

/** Bakery stock first, then central, FEFO within each. */
const byWarehouseThenFefo = (a: IngredientLot, b: IngredientLot): number => {
  const rank = LOCATION_RANK[lotLocation(a)] - LOCATION_RANK[lotLocation(b)];
  return rank !== 0 ? rank : byFefo(a, b);
};

/** Stock at the warehouse the lot would actually be sourced from. */
const sourceKg = (lot: IngredientLot): number => {
  switch (lotLocation(lot)) {
    case "bakery":
      return kgAt(lot, AT_BAKERY);
    case "central":
      return kgAt(lot, AT_CENTRAL);
    case "elsewhere":
      return Number(elsewhereStock(lot)?.remaining_kg ?? 0);
    default:
      return Number(lot.remaining_kg || 0);
  }
};

const lotOptionLabel = (lot: IngredientLot): string => {
  const supplier = lot.supplier || t("bez dobavljača");
  const expiry = lot.expiry_date
    ? t(" · rok {date}", { date: new Date(lot.expiry_date).toLocaleDateString(dateLocale()) })
    : "";
  const where = lotLocation(lot);
  if (where === "unbooked") {
    const missing = lot.has_stock_record === false ? t("bez knjižene zalihe") : t("nema zalihe");
    return `${lot.lot_number} (${supplier}) — ${missing}${expiry}`;
  }
  const warehouse =
    where === "bakery"
      ? t(WAREHOUSE_LABEL[AT_BAKERY])
      : where === "central"
        ? t(WAREHOUSE_LABEL[AT_CENTRAL])
        : elsewhereStock(lot)?.warehouse_code || t("drugo skladište");
  return `${lot.lot_number} (${supplier}) — ${sourceKg(lot).toFixed(2)} kg @ ${warehouse}${expiry}`;
};

/** One lot that has to be moved from the central warehouse to the bakery. */
interface TransferCandidate {
  lot_id: number;
  lot_number: string;
  ingredient_id: number | null;
  alias_id: number | null;
  ingredient_name: string;
  /** Everything the plan takes from this lot. */
  required_kg: number;
  at_bakery_kg: number;
  at_central_kg: number;
  /** What the transfer moves: the part missing at the bakery, capped by central stock. */
  transfer_kg: number;
  /** Still missing after the transfer, if the central warehouse cannot cover it. */
  shortage_kg: number;
  batch_refs: string[];
  expiry_date?: string;
}

/**
 * Lot assignment step of plan confirmation.
 *
 * By default one lot is chosen per (product, ingredient) and applied to every
 * batch of that product, which is how the bakery works in practice. A product
 * can be switched to "po zamjesu" to pick a different lot for each individual
 * batch (e.g. when a lot runs out mid-day).
 */
const LotAssignment: React.FC<LotAssignmentProps> = ({
  batches,
  timelineId,
  productionDate,
  onComplete,
  onCancel,
}) => {
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [lotPools, setLotPools] = useState<Record<string, IngredientLot[]>>({});
  const [productInfo, setProductInfo] = useState<ProductRecipeInfo[]>([]);
  const [batchesWithoutRecipe, setBatchesWithoutRecipe] = useState<string[]>([]);
  const [perBatchProducts, setPerBatchProducts] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createTransfer, setCreateTransfer] = useState(true);
  /** Lots the planner took out of the transfer; tracked as exclusions so lots
   *  added by a later selection are included by default. */
  const [excludedTransferLots, setExcludedTransferLots] = useState<number[]>([]);

  const loadBatchIngredients = useCallback(async () => {
    setLoading(true);
    try {
      // Every recipe of the day in ONE request, and only its material lines:
      // fetching the full recipe per distinct recipe made this step wait seconds
      // on mixer parameters and semi-finished phase trees it never reads.
      const recipeIds = Array.from(
        new Set(batches.map((b) => b.RecipeID).filter((id): id is number => !!id))
      );
      // Phase-material demands come from the server for every batch at once:
      // the normative (basis, scrap, pieces per batch) is evaluated there, so
      // planning, execution and costing all quote the same quantity.
      // Već dodijeljeni lotovi se učitavaju jer se ovaj ekran otvara i nad
      // potvrđenim planom: bez njih bi ponovno otvaranje izgledalo kao da
      // ništa nije dodijeljeno i planer bi nadopisivao vlastiti rad.
      const [recipes, phaseDemands, assigned] = await Promise.all([
        mesApi.getRecipeMaterialLines(recipeIds),
        mesApi.getPhaseMaterialDemandsForBatches(batches.map((b) => b.ID)),
        mesApi
          .getBatchMaterialLots(batches.map((b) => b.ID), { includePhaseMaterials: true })
          .catch(() => []),
      ]);
      const recipeById = new Map<number, RecipeMaterialLines>(
        recipes.map((r) => [r.recipe_id, r])
      );

      const demandsByBatch = new Map<number, typeof phaseDemands>();
      for (const demand of phaseDemands) {
        const list = demandsByBatch.get(demand.batchId) || [];
        list.push(demand);
        demandsByBatch.set(demand.batchId, list);
      }

      // Mjesto stavke se veže isto kao na serveru: recepturna sirovina po ID-u
      // sirovine, fazni materijal po normativnom redu faze.
      const assignedLotByRow = new Map<string, number>();
      for (const row of assigned) {
        if (row.materialLotId == null) continue;
        const key =
          row.sourcePhaseMaterialId != null
            ? `${row.batchId}:pm:${row.sourcePhaseMaterialId}`
            : row.ingredientId != null
              ? `${row.batchId}:ing:${row.ingredientId}`
              : null;
        if (key) assignedLotByRow.set(key, row.materialLotId);
      }

      const nextRows: AssignmentRow[] = [];
      const products = new Map<number, ProductRecipeInfo>();
      const missingRecipe: string[] = [];
      const ingredientIds = new Set<number>();
      const aliasIds = new Set<number>();

      for (const batch of batches) {
        const recipe = batch.RecipeID ? recipeById.get(batch.RecipeID) : undefined;
        const lines = recipe?.lines || [];

        if (lines.length === 0) {
          missingRecipe.push(batch.MixCode);
          continue;
        }

        const productKey = batch.ProductID ?? 0;
        const existing = products.get(productKey);
        if (existing) {
          existing.batch_count += 1;
        } else {
          const version = recipe?.recipe_version ? ` v${recipe.recipe_version}` : "";
          products.set(productKey, {
            product_key: productKey,
            product_name: batch.ProductName || t("Nepoznat proizvod"),
            recipe_label: `${recipe?.recipe_code || batch.RecipeCode || `#${batch.RecipeID}`}${version}`,
            batch_count: 1,
          });
        }

        // Scale by TOTAL dough mass: required_kg = bakerPct * (batchMass / Σ all %).
        // Σ must include every line (flour, water, salt, child recipes) because that is
        // the true baker-% denominator that maps % -> the batch's finalna masa.
        const sumPct = lines.reduce((sum, l) => sum + (Number(l.baker_percentage) || 0), 0);
        const scaleFactor = sumPct > 0 ? (batch.FinalMassKg || 0) / sumPct : 0;

        for (const ing of lines) {
          // Semi-finished products / sub-recipes (ChildRecipe lines) are produced as
          // their own batches with their own lots already assigned, so they must not
          // appear here. Same for any line without a concrete stock ingredient.
          if (ing.child_recipe_id || !ing.ingredient_id) continue;

          if (isWaterIngredient(ing.ingredient_type_name, ing.ingredient_name)) {
            continue;
          }

          ingredientIds.add(ing.ingredient_id);
          nextRows.push({
            kind: "ingredient",
            batch_id: batch.ID,
            batch_name: batch.MixCode,
            product_key: productKey,
            product_name: batch.ProductName || t("Nepoznat proizvod"),
            material_key: ingredientPoolKey(ing.ingredient_id),
            lot_pool_key: ingredientPoolKey(ing.ingredient_id),
            ingredient_id: ing.ingredient_id,
            alias_id: null,
            phase_material_id: null,
            phase_name: null,
            material_name: ing.ingredient_name || t("Nepoznata sirovina"),
            required_qty: (ing.baker_percentage || 0) * scaleFactor,
            unit_label: "kg",
            selected_lot_id: assignedLotByRow.get(`${batch.ID}:ing:${ing.ingredient_id}`) ?? null,
          });
        }

        for (const demand of demandsByBatch.get(batch.ID) || []) {
          aliasIds.add(demand.ingredientAliasId);
          nextRows.push({
            kind: "phase_material",
            batch_id: batch.ID,
            batch_name: batch.MixCode,
            product_key: productKey,
            product_name: batch.ProductName || t("Nepoznat proizvod"),
            // Two phases of one product may consume the same material in
            // different amounts, so the normative row — not the material — is
            // what identifies the line.
            material_key: `pm:${demand.phaseMaterialId}`,
            lot_pool_key: aliasPoolKey(demand.ingredientAliasId),
            ingredient_id: demand.ingredientId,
            alias_id: demand.ingredientAliasId,
            phase_material_id: demand.phaseMaterialId,
            phase_name: demand.phaseTypeName,
            material_name: demand.materialName,
            required_qty: demand.requiredQty,
            unit_label: demand.unitCode || "kg",
            selected_lot_id: assignedLotByRow.get(`${batch.ID}:pm:${demand.phaseMaterialId}`) ?? null,
          });
        }
      }

      // All lots for all materials in ONE request (previously one per line).
      const lots: IngredientLot[] =
        ingredientIds.size || aliasIds.size
          ? (await mesApi.getIngredientLots({
              ingredient_ids: Array.from(ingredientIds),
              alias_ids: Array.from(aliasIds),
              is_active: true,
              // Needed to offer bakery stock before central stock, and to know
              // which lots must be transferred before the day can be produced.
              stock_scope: "production",
            })) || []
          : [];

      const pool: Record<string, IngredientLot[]> = {};
      lots.forEach((lot) => {
        if ((lot.remaining_kg || 0) <= 0) return;
        // A lot can answer both pools: packaging lots carry only an alias, while
        // a raw material lot may carry the ingredient and an alias from the ERP.
        if (lot.ingredient_id) {
          (pool[ingredientPoolKey(Number(lot.ingredient_id))] ||= []).push(lot);
        }
        if (lot.ingredient_alias_id) {
          (pool[aliasPoolKey(Number(lot.ingredient_alias_id))] ||= []).push(lot);
        }
      });
      Object.values(pool).forEach((list) => list.sort(byWarehouseThenFefo));

      // Lot dodijeljen prije, a u međuvremenu potrošen, više nije među
      // ponuđenima — takav odabir se pušta prazan da ga planer zamijeni, jer
      // bi inače stajao u padajućem popisu kao prazna vrijednost.
      const offered = new Set(Object.values(pool).flatMap((list) => list.map((lot) => lot.lot_id)));
      const sanitized = nextRows.map((r) =>
        r.selected_lot_id != null && !offered.has(r.selected_lot_id)
          ? { ...r, selected_lot_id: null }
          : r
      );

      setRows(sanitized);
      setLotPools(pool);
      setProductInfo(Array.from(products.values()));
      setBatchesWithoutRecipe(missingRecipe);
    } catch (error: any) {
      toast.error(t("Greška pri učitavanju receptura: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  }, [batches]);

  useEffect(() => {
    loadBatchIngredients();
  }, [loadBatchIngredients]);

  const isPerBatch = useCallback(
    (productKey: number) => perBatchProducts.includes(productKey),
    [perBatchProducts]
  );

  const togglePerBatch = (productKey: number) => {
    setPerBatchProducts((prev) =>
      prev.includes(productKey) ? prev.filter((k) => k !== productKey) : [...prev, productKey]
    );
  };

  const lotsFor = useCallback(
    (poolKey: string): IngredientLot[] => lotPools[poolKey] || [],
    [lotPools]
  );

  /** Apply a lot to every batch of a product (shared-material mode). */
  const selectLotForProduct = (productKey: number, materialKey: string, lotId: number | null) => {
    setRows((prev) =>
      prev.map((r) =>
        r.product_key === productKey && r.material_key === materialKey
          ? { ...r, selected_lot_id: lotId }
          : r
      )
    );
  };

  /** Apply a lot to a single batch (per-batch mode). */
  const selectLotForBatch = (batchId: number, materialKey: string, lotId: number | null) => {
    setRows((prev) =>
      prev.map((r) =>
        r.batch_id === batchId && r.material_key === materialKey
          ? { ...r, selected_lot_id: lotId }
          : r
      )
    );
  };

  /**
   * FEFO auto-assign. Groups follow the current mode, so a product in shared
   * mode gets one lot covering all its batches, while a per-batch product is
   * assigned batch by batch.
   */
  const handleAutoAssign = () => {
    const requiredByGroup = new Map<string, number>();
    const groupKey = (r: AssignmentRow) =>
      isPerBatch(r.product_key)
        ? `b:${r.batch_id}:${r.material_key}`
        : `p:${r.product_key}:${r.material_key}`;

    rows.forEach((r) => {
      requiredByGroup.set(groupKey(r), (requiredByGroup.get(groupKey(r)) || 0) + r.required_qty);
    });

    const lotByGroup = new Map<string, number | null>();
    rows.forEach((r) => {
      const key = groupKey(r);
      if (lotByGroup.has(key)) return;
      // Candidates are already ordered bakery-before-central, so the first lot
      // that covers the whole group is the closest one that does — a short lot at
      // the bakery is not worth an assignment we know cannot be filled.
      const candidates = lotsFor(r.lot_pool_key);
      const needed = requiredByGroup.get(key) || 0;
      const sufficient = candidates.find((lot) => sourceKg(lot) >= needed);
      lotByGroup.set(key, sufficient?.lot_id ?? candidates[0]?.lot_id ?? null);
    });

    setRows((prev) =>
      prev.map((r) => ({ ...r, selected_lot_id: lotByGroup.get(groupKey(r)) ?? null }))
    );
    toast.success(t("Lotovi dodijeljeni po FEFO logici, zaliha pekare ima prednost"));
  };

  /**
   * Nedodijeljene stavke se dijele na one koje planer MOŽE riješiti i one za
   * koje na zalihi nema niti jednog lota.
   *
   * Ta razlika je bitna jer je dodjela prije bila sve-ili-ništa: jedna sirovina
   * bez zalihe blokirala je spremanje cijelog dana, pa je planiranje završavalo
   * na "Potvrdi bez lotova" i mikser nije dobio ni jedan lot. Stavka bez
   * ponuđenog lota se zato samo prijavi i preskoči.
   */
  const { pendingCount, unavailableCount } = useMemo(() => {
    let pending = 0;
    let unavailable = 0;
    for (const r of rows) {
      if (r.selected_lot_id) continue;
      if ((lotPools[r.lot_pool_key] || []).length === 0) unavailable++;
      else pending++;
    }
    return { pendingCount: pending, unavailableCount: unavailable };
  }, [rows, lotPools]);

  const assignedLotCount = useMemo(
    () =>
      new Set(rows.map((r) => r.selected_lot_id).filter((id): id is number => id != null)).size,
    [rows]
  );

  const lotById = useMemo(() => {
    const map = new Map<number, IngredientLot>();
    Object.values(lotPools).forEach((list) => list.forEach((lot) => map.set(lot.lot_id, lot)));
    return map;
  }, [lotPools]);

  /**
   * Assigned lots whose stock is not (fully) at the bakery but is at the central
   * warehouse. These are what the inter-warehouse transfer has to move before the
   * day can actually be mixed.
   */
  const transferCandidates = useMemo<TransferCandidate[]>(() => {
    const byLot = new Map<number, TransferCandidate>();

    for (const row of rows) {
      if (!row.selected_lot_id) continue;
      const lot = lotById.get(row.selected_lot_id);
      if (!lot) continue;

      const existing = byLot.get(lot.lot_id);
      if (existing) {
        existing.required_kg += row.required_qty;
        if (!existing.batch_refs.includes(row.batch_name)) {
          existing.batch_refs.push(row.batch_name);
        }
        continue;
      }
      byLot.set(lot.lot_id, {
        lot_id: lot.lot_id,
        lot_number: lot.lot_number,
        ingredient_id: row.ingredient_id,
        alias_id: row.alias_id ?? lot.ingredient_alias_id ?? null,
        ingredient_name: row.material_name,
        required_kg: row.required_qty,
        at_bakery_kg: kgAt(lot, AT_BAKERY),
        at_central_kg: kgAt(lot, AT_CENTRAL),
        transfer_kg: 0,
        shortage_kg: 0,
        batch_refs: [row.batch_name],
        expiry_date: lot.expiry_date,
      });
    }

    return Array.from(byLot.values())
      .map((c) => {
        // Whatever already sits at the bakery does not have to travel; the rest
        // does, as far as the central warehouse can cover it.
        const missingAtBakery = Math.max(0, c.required_kg - c.at_bakery_kg);
        const transfer_kg = Math.min(missingAtBakery, c.at_central_kg);
        return {
          ...c,
          transfer_kg,
          shortage_kg: missingAtBakery - transfer_kg,
        };
      })
      .filter((c) => c.transfer_kg > STOCK_EPSILON)
      .sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name, compareLocale()));
  }, [rows, lotById]);

  const selectedTransferLines = useMemo(
    () => transferCandidates.filter((c) => !excludedTransferLots.includes(c.lot_id)),
    [transferCandidates, excludedTransferLots]
  );

  const toggleTransferLot = (lotId: number) => {
    setExcludedTransferLots((prev) =>
      prev.includes(lotId) ? prev.filter((id) => id !== lotId) : [...prev, lotId]
    );
  };

  /**
   * Books the transfer for the material coming from the central warehouse.
   *
   * Source and destination warehouses are deliberately left to the server, which
   * owns that pairing. Failure is reported but not rethrown: the lot assignment
   * is already saved at this point, and the transfer can be created by hand from
   * the warehouse module, so there is nothing to gain from blocking the plan.
   */
  const createTransferDocument = async () => {
    try {
      const transfer = await mesApi.createInterWarehouseTransfer({
        production_date: productionDate,
        timeline_id: timelineId,
        notes: `Materijal s centralnog skladišta za plan ${productionDate}`,
        lines: selectedTransferLines.map((c) => ({
          ingredient_id: c.ingredient_id ?? undefined,
          ingredient_alias_id: c.alias_id ?? undefined,
          material_lot_id: c.lot_id,
          lot_number: c.lot_number,
          quantity_kg: round3(c.transfer_kg),
          required_quantity_kg: round3(c.required_kg),
          expiry_date: c.expiry_date ? c.expiry_date.split("T")[0] : undefined,
          batch_refs: c.batch_refs.join(", "),
          notes:
            c.shortage_kg > SHORTAGE_EPSILON
              ? `Nedostaje ${round3(c.shortage_kg).toFixed(2)} kg na centralnom skladištu`
              : undefined,
        })),
      });
      const number = transfer?.transfer_number ? ` ${transfer.transfer_number}` : "";
      toast.success(t("Međuskladišnica{number} kreirana ({count} stavki)", { number, count: selectedTransferLines.length }));
    } catch (error: any) {
      toast.error(t("Dodjela lotova je spremljena, ali međuskladišnica nije kreirana: {message}", { message: error.message }));
    }
  };

  const handleConfirm = async () => {
    if (pendingCount > 0) {
      toast.error(t("{count} stavki bez dodijeljenog lota", { count: pendingCount }));
      return;
    }

    setSaving(true);
    try {
      const assignmentsByBatch = new Map<
        number,
        {
          lot_id: number;
          ingredient_id: number | null;
          planned_kg: number;
          source: string;
          ingredient_alias_id?: number | null;
          source_phase_material_id?: number | null;
        }[]
      >();
      for (const r of rows) {
        if (!r.selected_lot_id) continue;
        const perBatch = isPerBatch(r.product_key);
        const list = assignmentsByBatch.get(r.batch_id) || [];
        list.push({
          lot_id: r.selected_lot_id,
          ingredient_id: r.ingredient_id,
          planned_kg: r.required_qty,
          // The phase-material marker also has to survive here: without it the
          // line would later be costed as an off-recipe raw material.
          ingredient_alias_id: r.alias_id,
          source_phase_material_id: r.phase_material_id,
          source:
            r.kind === "phase_material"
              ? perBatch
                ? "phase_material_batch"
                : "phase_material_plan"
              : perBatch
                ? "manual_batch"
                : "manual_product",
        });
        assignmentsByBatch.set(r.batch_id, list);
      }

      // Batches are independent of each other, so persist them concurrently.
      await Promise.all(
        Array.from(assignmentsByBatch.entries()).map(([batchId, assignments]) =>
          mesApi.createBatchLotConsumption(batchId, assignments)
        )
      );

      toast.success(t("Dodjela lotova spremljena"));

      if (createTransfer && selectedTransferLines.length > 0) {
        await createTransferDocument();
      }

      onComplete();
    } catch (error: any) {
      toast.error(t("Spremanje dodjele lotova nije uspjelo: {message}", { message: error.message }));
    } finally {
      setSaving(false);
    }
  };

  interface SharedLine {
    material_key: string;
    lot_pool_key: string;
    material_name: string;
    phase_name: string | null;
    unit_label: string;
    required_qty: number;
    lotIds: Set<number | null>;
  }

  /** Rows of one product collapsed to one line per material (shared mode). */
  const sharedLines = (productKey: number, kind: MaterialKind): SharedLine[] => {
    const byMaterial = new Map<string, SharedLine>();
    rows
      .filter((r) => r.product_key === productKey && r.kind === kind)
      .forEach((r) => {
        const entry = byMaterial.get(r.material_key);
        if (entry) {
          entry.required_qty += r.required_qty;
          entry.lotIds.add(r.selected_lot_id);
        } else {
          byMaterial.set(r.material_key, {
            material_key: r.material_key,
            lot_pool_key: r.lot_pool_key,
            material_name: r.material_name,
            phase_name: r.phase_name,
            unit_label: r.unit_label,
            required_qty: r.required_qty,
            lotIds: new Set([r.selected_lot_id]),
          });
        }
      });
    return Array.from(byMaterial.values());
  };

  /** Rows of one product grouped by batch (per-batch mode). */
  const batchGroups = (productKey: number, kind: MaterialKind) => {
    const byBatch = new Map<number, { batch_id: number; batch_name: string; lines: AssignmentRow[] }>();
    rows
      .filter((r) => r.product_key === productKey && r.kind === kind)
      .forEach((r) => {
        const entry = byBatch.get(r.batch_id);
        if (entry) entry.lines.push(r);
        else byBatch.set(r.batch_id, { batch_id: r.batch_id, batch_name: r.batch_name, lines: [r] });
      });
    return Array.from(byBatch.values());
  };

  const hasPhaseMaterials = useMemo(
    () => rows.some((r) => r.kind === "phase_material"),
    [rows]
  );

  const renderLotSelect = (
    poolKey: string,
    requiredKg: number,
    selectedLotId: number | null,
    mixed: boolean,
    onSelect: (lotId: number | null) => void
  ) => {
    const options = lotsFor(poolKey);
    if (options.length === 0) {
      return <span className="no-lots-warning">{t("⚠ Nema dostupnih lotova")}</span>;
    }
    return (
      <select
        value={mixed ? "" : selectedLotId || ""}
        onChange={(e) => onSelect(e.target.value ? parseInt(e.target.value, 10) : null)}
        className={!selectedLotId || mixed ? "unassigned-select" : ""}
      >
        <option value="">{mixed ? t("-- Različiti lotovi --") : t("-- Odaberi lot --")}</option>
        {options.map((lot) => (
          <option key={lot.lot_id} value={lot.lot_id}>
            {lotOptionLabel(lot)}
            {sourceKg(lot) < requiredKg ? " ⚠" : ""}
          </option>
        ))}
      </select>
    );
  };

  const renderStatus = (poolKey: string, requiredKg: number, lotId: number | null, mixed: boolean) => {
    if (mixed) return <span className="status-badge mixed">{t("Po zamjesu")}</span>;
    if (!lotId) return <span className="status-badge unassigned">{t("Nedodijeljeno")}</span>;
    const lot = lotsFor(poolKey).find((l) => l.lot_id === lotId);
    if (lot && sourceKg(lot) < requiredKg) {
      return <span className="status-badge warning">{t("⚠ Nedovoljna zaliha")}</span>;
    }
    const where = lot ? lotLocation(lot) : "unknown";
    if (where === "central") {
      return (
        <span
          className="status-badge transfer"
          title={t("Zaliha je na centralnom skladištu — treba je prenijeti u pekaru međuskladišnicom")}
        >
          {t("⇄ Centralno")}
        </span>
      );
    }
    if (where === "elsewhere") {
      const at = elsewhereStock(lot);
      return (
        <span
          className="status-badge warning"
          title={t("Zaliha nije ni u pekari ni na centralnom skladištu, nego na {warehouse}", { warehouse: at?.warehouse_name || t("drugom skladištu") })}
        >
          {t("⚠ Na {warehouse}", { warehouse: at?.warehouse_code || t("drugom skladištu") })}
        </span>
      );
    }
    if (where === "unbooked") {
      // Razlika je bitna skladištaru: lot bez ijednog knjiženja možda fizički
      // postoji, dok lot s knjiženjima i nulom nigdje je doista prazan.
      const neverBooked = lot?.has_stock_record === false;
      return (
        <span
          className="status-badge warning"
          title={
            neverBooked
              ? t("Za ovaj lot nema nijednog knjiženja na skladište, pa mu se stanje ne može izračunati. Zaliha možda fizički postoji — provjeri sa skladištarom.")
              : t("Lot ima knjiženja, ali ni na jednom skladištu nema preostale zalihe")
          }
        >
          {neverBooked ? t("⚠ Bez knjižene zalihe") : t("⚠ Nema zalihe")}
        </span>
      );
    }
    return <span className="status-badge ok">✓ OK</span>;
  };

  /**
   * Phase materials get a Faza column and carry their unit in the cell, because
   * a normative can be expressed in pieces while the recipe is always in kg.
   */
  const renderQty = (qty: number, unit: string, showUnit: boolean) =>
    showUnit ? `${qty.toFixed(2)} ${unit}` : qty.toFixed(2);

  const renderPerBatchTable = (lines: AssignmentRow[], phase: boolean) => (
    <div className="mes-table-wrap">
      <table className="lot-assignment-table">
        <thead>
          <tr>
            {phase && <th>{t("Faza")}</th>}
            <th>{phase ? t("Materijal") : t("Sirovina")}</th>
            <th>{phase ? t("Potrebno") : t("Potrebno (kg)")}</th>
            <th>{t("Lot")}</th>
            <th>{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={`${line.batch_id}-${line.material_key}`}>
              {phase && <td>{line.phase_name || "—"}</td>}
              <td>{line.material_name}</td>
              <td className="number-cell">
                {renderQty(line.required_qty, line.unit_label, phase)}
              </td>
              <td>
                {renderLotSelect(
                  line.lot_pool_key,
                  line.required_qty,
                  line.selected_lot_id,
                  false,
                  (lotId) => selectLotForBatch(line.batch_id, line.material_key, lotId)
                )}
              </td>
              <td>
                {renderStatus(
                  line.lot_pool_key,
                  line.required_qty,
                  line.selected_lot_id,
                  false
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSharedTable = (productKey: number, lines: SharedLine[], phase: boolean) => (
    <div className="mes-table-wrap">
      <table className="lot-assignment-table">
        <thead>
          <tr>
            {phase && <th>{t("Faza")}</th>}
            <th>{phase ? t("Materijal") : t("Sirovina")}</th>
            <th>{phase ? t("Potrebno ukupno") : t("Potrebno ukupno (kg)")}</th>
            <th>{t("Lot (za sve zamjese)")}</th>
            <th>{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const mixed = line.lotIds.size > 1;
            const lotId = mixed ? null : Array.from(line.lotIds)[0] ?? null;
            return (
              <tr key={`${productKey}-${line.material_key}`}>
                {phase && <td>{line.phase_name || "—"}</td>}
                <td>{line.material_name}</td>
                <td className="number-cell">
                  {renderQty(line.required_qty, line.unit_label, phase)}
                </td>
                <td>
                  {renderLotSelect(line.lot_pool_key, line.required_qty, lotId, mixed, (selected) =>
                    selectLotForProduct(productKey, line.material_key, selected)
                  )}
                </td>
                <td>{renderStatus(line.lot_pool_key, line.required_qty, lotId, mixed)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="lot-assignment-loading">
        <p>{t("Učitavanje receptura i lotova…")}</p>
      </div>
    );
  }

  return (
    <div className="lot-assignment-container">
      <div className="lot-assignment-header">
        <div>
          <h3>{t("Dodjela lotova sirovina")}</h3>
          <p className="lot-assignment-hint">
            {t("Lot se prema zadanom bira jednom po proizvodu i primjenjuje na sve njegove zamjese. Uključi")} <strong>{t("Po zamjesu")}</strong> {t("za proizvod kojem želiš dodijeliti različite sirovine po zamjesu. Prvo se nude lotovi iz pekare, a zatim oni s centralnog skladišta.")}
            {hasPhaseMaterials && (
              <>
                {" "}
                {t("Uz sirovine zamjesa potvrđuju se i")} <strong>{t("dodatne sirovine po fazi")}</strong>{" "}
                {t("(repro i ambalaža) s količinom iz normativa proizvoda.")}
              </>
            )}
          </p>
        </div>
        <button className="btn-auto-assign" onClick={handleAutoAssign} disabled={saving}>
          {t("Automatski (FEFO)")}
        </button>
      </div>

      {batchesWithoutRecipe.length > 0 && (
        <div className="lot-assignment-alert">
          {t("⚠ Bez recepture: {list} — za te zamjese lotovi se ne mogu dodijeliti.", { list: batchesWithoutRecipe.join(", ") })}
        </div>
      )}

      <div className="lot-assignment-body">
        {productInfo.length === 0 ? (
          <p className="empty-state">{t("Nema sirovina za dodjelu lotova")}</p>
        ) : (
          productInfo.map((product) => {
            const perBatch = isPerBatch(product.product_key);
            const phaseLines = sharedLines(product.product_key, "phase_material");
            return (
              <div key={product.product_key} className="batch-section">
                <div className="batch-header">
                  <div>
                    <h4>{product.product_name}</h4>
                    <span className="recipe-chip">
                      {t("Receptura {recipe} · {count} zamjesa", { recipe: product.recipe_label, count: product.batch_count })}
                    </span>
                  </div>
                  <label className="per-batch-toggle">
                    <input
                      type="checkbox"
                      checked={perBatch}
                      onChange={() => togglePerBatch(product.product_key)}
                    />
                    {t("Po zamjesu")}
                  </label>
                </div>

                {perBatch
                  ? batchGroups(product.product_key, "ingredient").map((group) => (
                      <div key={group.batch_id} className="batch-subsection">
                        <h5>{group.batch_name}</h5>
                        {renderPerBatchTable(group.lines, false)}
                      </div>
                    ))
                  : renderSharedTable(
                      product.product_key,
                      sharedLines(product.product_key, "ingredient"),
                      false
                    )}

                {phaseLines.length > 0 && (
                  <div className="phase-material-block">
                    <h5 className="phase-material-title">
                      {t("Dodatne sirovine po fazi")}
                      <span className="phase-material-note">
                        {t("repro i ambalaža iz normativa proizvoda")}
                      </span>
                    </h5>
                    {perBatch
                      ? batchGroups(product.product_key, "phase_material").map((group) => (
                          <div key={group.batch_id} className="batch-subsection">
                            <h5>{group.batch_name}</h5>
                            {renderPerBatchTable(group.lines, true)}
                          </div>
                        ))
                      : renderSharedTable(product.product_key, phaseLines, true)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {transferCandidates.length > 0 && (
        <div className="lot-transfer-panel">
          <label className="lot-transfer-toggle">
            <input
              type="checkbox"
              checked={createTransfer}
              onChange={() => setCreateTransfer((prev) => !prev)}
            />
            <span>
              {t("Kreiraj međuskladišnicu iz")} <strong>ZAGREB-MATERIJALNO CENTRALNO</strong> {t("u")}{" "}
              <strong>ZAGREB-PEKARA MATERIJALNO</strong>
            </span>
          </label>
          <p className="lot-transfer-hint">
            {t("Na centralnom skladištu je {from} od {total} dodijeljenih lotova. Bez prijenosa ih skladištar ne može izdati u zamjes. Odznači stavku koju ne želiš na međuskladišnici.", { from: transferCandidates.length, total: assignedLotCount })}
          </p>

          <div className="mes-table-wrap">
            <table className="lot-transfer-table">
              <thead>
                <tr>
                  <th />
                  <th>{t("Materijal")}</th>
                  <th>{t("Lot")}</th>
                  <th>{t("Za prijenos (kg)")}</th>
                  <th>{t("Potrebno (kg)")}</th>
                  <th>{t("Na centralnom (kg)")}</th>
                  <th>{t("Zamjese")}</th>
                </tr>
              </thead>
              <tbody>
                {transferCandidates.map((c) => {
                  const included = !excludedTransferLots.includes(c.lot_id);
                  return (
                    <tr key={c.lot_id} className={included ? "" : "excluded"}>
                      <td>
                        <input
                          type="checkbox"
                          checked={included}
                          disabled={!createTransfer}
                          onChange={() => toggleTransferLot(c.lot_id)}
                        />
                      </td>
                      <td>{c.ingredient_name}</td>
                      <td>{c.lot_number}</td>
                      <td className="number-cell">{c.transfer_kg.toFixed(2)}</td>
                      <td className="number-cell">{c.required_kg.toFixed(2)}</td>
                      <td className="number-cell">
                        {c.at_central_kg.toFixed(2)}
                        {c.shortage_kg > SHORTAGE_EPSILON && (
                          <span
                            className="transfer-shortage"
                            title={t("Nedostaje {kg} kg na centralnom skladištu", { kg: c.shortage_kg.toFixed(2) })}
                          >
                            {" "}
                            ⚠
                          </span>
                        )}
                      </td>
                      <td className="batch-refs-cell" title={c.batch_refs.join(", ")}>
                        {c.batch_refs.length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="lot-assignment-footer">
        <span className="lot-assignment-summary">
          {pendingCount > 0 ? t("{count} nedodijeljenih stavki", { count: pendingCount }) : t("Sve stavke imaju lot")}
          {unavailableCount > 0 && (
            <em className="lot-assignment-transfer-note">
              {" "}
              · {t("{count} bez zalihe (preskače se)", { count: unavailableCount })}
            </em>
          )}
          {createTransfer && selectedTransferLines.length > 0 && (
            <em className="lot-assignment-transfer-note">
              {" "}
              · {t("međuskladišnica sa {count} stavki", { count: selectedTransferLines.length })}
            </em>
          )}
        </span>
        <button className="btn-cancel" onClick={onCancel} disabled={saving}>
          {t("Odustani")}
        </button>
        <button className="btn-confirm" onClick={handleConfirm} disabled={saving}>
          {saving ? t("Spremanje…") : t("Potvrdi i spremi")}
        </button>
      </div>

    </div>
  );
};

export default LotAssignment;
